import { useState } from 'react';

import { referralLink } from './share-message';
import { useReferralSummary } from './useReferralSummary';

interface InviteScreenProps {
  /** Адрес приложения; параметром — чтобы поведение проверялось без window. */
  origin?: string | undefined;
}

/**
 * Приглашения.
 *
 * Экран ничего не начисляет и не обещает награду за отправку ссылки:
 * реферал становится награждённым только после первой успешной генерации
 * приглашённого, и статистика показывает это разделение прямо. Размер
 * награды здесь не назван — это серверная настройка (referral_reward),
 * и зашивать её число в интерфейс нельзя.
 */
export function InviteScreen({ origin }: InviteScreenProps) {
  const { data, isPending, isError, refetch } = useReferralSummary();
  const [copied, setCopied] = useState(false);

  if (isPending) {
    return <p className="px-6 py-8 text-text-2">Загрузка приглашений…</p>;
  }

  if (isError) {
    return (
      <div className="px-6 py-8">
        <p className="text-danger">Не удалось загрузить приглашения. Попробуйте ещё раз.</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-2 text-sm text-accent hover:underline"
        >
          Повторить
        </button>
      </div>
    );
  }

  // Профиль заводится сразу после входа, но занимает мгновение — до
  // этого кода приглашения ещё нет.
  if (!data) {
    return <p className="px-6 py-8 text-text-2">Готовим вашу ссылку…</p>;
  }

  const link = referralLink(origin ?? window.location.origin, data.code);

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-6 px-6 py-8">
      <div className="flex flex-col gap-2">
        <p className="text-sm text-text-2">Ваша ссылка для приглашения</p>
        <p className="rounded-card border border-border bg-surface px-3 py-2 text-sm break-all text-text">
          {link}
        </p>
        <button
          type="button"
          onClick={() => void copy()}
          className="btn btn-secondary self-start px-4 py-2 text-sm"
        >
          {copied ? 'Ссылка скопирована' : 'Скопировать ссылку'}
        </button>
      </div>

      <dl className="grid grid-cols-3 gap-2 text-center">
        <Stat label="Приглашено" value={data.invited} />
        <Stat label="Награда начислена" value={data.rewarded} />
        <Stat label="Ждут первого портрета" value={data.waiting} />
      </dl>

      <p className="text-xs text-text-3">
        Кредиты приходят, когда приглашённый создаст свой первый портрет. За переход по
        ссылке и за саму отправку приглашения награды нет.
      </p>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-card border border-border bg-surface p-3">
      <dt className="text-xs text-text-3">{label}</dt>
      <dd aria-label={label} className="mt-1 text-xl text-text tabular-nums">
        {value}
      </dd>
    </div>
  );
}
